import { Box, Typography, useTheme, Chip, IconButton, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Drawer, Paper, Stack, Grid, Divider, Button } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { TabularIcon, ImagingClassificationIcon, ImagingSegmentationIcon } from '../../components/TaskTypeIcons';
import AppsIcon from '@mui/icons-material/Apps';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useEffect, useState, useMemo } from "react";
import { tokens } from "../../theme";
import { LoadingState, EmptyState } from "../../components/states";
import { MODEL_CATALOG } from "../../data/modelCatalog";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import MemoryIcon from '@mui/icons-material/Memory';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CodeIcon from '@mui/icons-material/Code';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
// Sidebar uses PsychologyAltOutlinedIcon for the Learning Models route — mirror
// it here so the page header matches the sidebar entry.
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import { useAppContext } from "../../contexts/AppContext";
import { useTranslation } from "react-i18next";
import HeaderWithControls from "../../components/HeaderWithControls";
import PageToolbar from "../../components/PageToolbar";

const Models = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  
  // Use shared context
  const { models, modelsLoading: loading, currentModel, config } = useAppContext();
  
  // State for controls
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [expandedModelId, setExpandedModelId] = useState(null);

  // Available models definition
  // Canonical catalog now lives in src/data/modelCatalog.js so the /models
  // page, the Overview hero banner, and the Overview stat card can never drift.
  const availableModels = MODEL_CATALOG;

  // Set initial filter based on config processing type
  // useEffect(() => {
  //   const configProcessingType = config?.dataset_config?.processing_type || config?.processing_type;
  //   if (configProcessingType && ['tabular', 'imaging', 'segmentation'].includes(configProcessingType)) {
  //     setFilterType(configProcessingType);
  //   }
  // }, [config]);

  // Handle row expansion
  const handleExpandClick = (modelId) => {
    setExpandedModelId(expandedModelId === modelId ? null : modelId);
  };

  // Helper: Get segmentation metadata
  // Helper: Get segmentation metadata
const getSegmentationModelMetadata = (modelId) => {
  const segmentationModels = {
    'unet': {
      architecture: 'U-Net',
      description: 'U-Net is a convolutional neural network that was developed for biomedical image segmentation. The network consists of a contracting path to capture context and a symmetric expanding path that enables precise localization.',
      complexity: 'Medium',
      use_case: 'Medical image segmentation',
      applicable_scenarios: ['Medical imaging segmentation', 'Biomedical image analysis', 'Cell and tissue segmentation'],
      limitations: ['Requires paired image-mask training data', 'Memory intensive for large images'],
      parameters: '~31M',
      training_time: 'Fast',
      interpretability: 'High',
      resources: {
        docs: 'https://paperswithcode.com/method/u-net',
        paper: 'https://arxiv.org/abs/1505.04597',
        code: 'https://github.com/milesial/Pytorch-UNet'
      }
    },
    'unetpp': {
      architecture: 'U-Net++',
      description: 'U-Net++ is an enhanced version of U-Net with nested skip connections and deep supervision. It provides better feature propagation and improved segmentation accuracy.',
      complexity: 'High',
      use_case: 'Advanced medical segmentation',
      applicable_scenarios: ['Complex medical imaging segmentation', 'Multi-organ segmentation', 'High-precision boundary detection'],
      limitations: ['Computationally intensive', 'Slower training and inference'],
      parameters: '~45M',
      training_time: 'Slow',
      interpretability: 'Medium',
      resources: {
        docs: 'https://paperswithcode.com/method/unet-plus-plus',
        paper: 'https://arxiv.org/abs/1807.10165',
        code: 'https://github.com/MrGiovanni/UNetPlusPlus'
      }
    },
    'fpn': {
      architecture: 'Feature Pyramid Network',
      description: 'Feature Pyramid Network (FPN) builds feature pyramids with lateral connections to combine low-resolution, semantically strong features with high-resolution, semantically weak features.',
      complexity: 'Medium',
      use_case: 'Multi-scale object segmentation',
      applicable_scenarios: ['Multi-scale segmentation', 'Objects of varying sizes'],
      limitations: ['May struggle with very fine details', 'Requires careful hyperparameter tuning'],
      parameters: '~25M',
      training_time: 'Fast',
      interpretability: 'Medium',
      resources: {
        docs: 'https://pytorch.org/vision/main/models/generated/torchvision.models.detection.maskrcnn_resnet50_fpn.html',
        paper: 'https://arxiv.org/abs/1612.03144',
        code: 'https://github.com/pytorch/vision/blob/main/torchvision/models/detection/faster_rcnn.py'
      }
    }
  };
  return segmentationModels[modelId] || null;
};

// Helper: Get tabular metadata
const getTabularModelMetadata = (modelId) => {
  const tabularModels = {
    'mlp': {
      architecture: 'Multi-Layer Perceptron',
      description: 'A classical fully connected feedforward neural network. It consists of an input layer, one or more hidden layers, and an output layer. MLPs are capable of learning complex non-linear relationships in data.',
      complexity: 'Low',
      use_case: 'General purpose classification and regression tasks',
      applicable_scenarios: ['Tabular data classification', 'Regression analysis', 'Simple pattern recognition'],
      limitations: ['Prone to overfitting on small datasets', 'Ignores spatial/temporal structure', 'Sensitive to feature scaling'],
      parameters: 'Variable',
      interpretability: 'Medium',
      resources: {
        docs: 'https://scikit-learn.org/stable/modules/neural_networks_supervised.html',
        paper: 'https://web.stanford.edu/class/psych209/Readings/RumelhartEtAlParallelDistributedProcessing1986.pdf',
        code: 'https://github.com/scikit-learn/scikit-learn/blob/main/sklearn/neural_network/_multilayer_perceptron.py'
      }
    },
    'tabnet': {
      architecture: 'TabNet',
      description: 'Attributes the interpretability of decision trees to deep learning. TabNet uses sequential attention to choose which features to reason from at each decision step, enabling interpretability and efficient learning.',
      complexity: 'High',
      use_case: 'Tabular data with feature selection requirements',
      applicable_scenarios: ['High-dimensional tabular data', 'Explainable AI requirements', 'Structured data learning'],
      limitations: ['Computationally expensive to train', 'Requires careful hyperparameter tuning', 'May be overkill for simple datasets'],
      parameters: '~100K-1M',
      interpretability: 'High',
      resources: {
        docs: 'https://cloud.google.com/blog/products/ai-machine-learning/google-cloud-ai-research-new-deep-learning-model-for-tabular-data-tabnet',
        paper: 'https://arxiv.org/abs/1908.07442',
        code: 'https://github.com/dreamquark-ai/tabnet'
      }
    },
    'fttransformer': {
      architecture: 'FT-Transformer',
      description: 'Feature Tokenizer + Transformer. A simple adaptation of the Transformer architecture for tabular data. It transforms all features (categorical and numerical) into embeddings and processes them through a stack of Transformer layers.',
      complexity: 'High',
      use_case: 'Complex tabular data relationships and patterns',
      applicable_scenarios: ['Complex tabular datasets', ' Heterogeneous feature sets', 'Tasks requiring state-of-the-art accuracy'],
      limitations: ['High computational cost', 'Slow inference speed', 'Requires large datasets to outperform tree-based models'],
      parameters: '~500K-5M',
      interpretability: 'Medium',
      resources: {
        docs: 'https://paperswithcode.com/method/ft-transformer',
        paper: 'https://arxiv.org/abs/2106.11959',
        code: 'https://github.com/Yura52/rtdl'
      }
    },
    'logreg': {
      architecture: 'Logistic Regression',
      description: 'A fundamental statistical method for binary classification. It models the probability of a binary outcome based on one or more independent variables.',
      complexity: 'Low',
      use_case: 'Linear relationships, baseline model, interpretable predictions',
      applicable_scenarios: ['Binary classification', 'Baseline performance establishment', 'High interpretability requirements'],
      limitations: ['Assumes linear relationship between features and log-odds', 'Cannot capture complex non-linear patterns'],
      parameters: '~1K-10K',
      interpretability: 'Very High',
      resources: {
        docs: 'https://scikit-learn.org/stable/modules/linear_model.html#logistic-regression',
        paper: 'https://www.sciencedirect.com/topics/medicine-and-dentistry/logistic-regression-analysis',
        code: 'https://github.com/scikit-learn/scikit-learn/blob/main/sklearn/linear_model/_logistic.py'
      }
    }
  };
  return tabularModels[modelId] || null;
};

// Helper: Get imaging metadata
const getImagingModelMetadata = (modelId) => {
  const imagingModels = {
    'convnext': {
      architecture: 'ConvNeXt',
      description: 'ConvNeXt is a modern convolutional neural network (CNN) architecture inspired by Vision Transformers, optimized for image classification tasks. It utilizes depthwise separable convolutions, larger receptive fields, and fewer activation functions.',
      complexity: 'High',
      use_case: 'Image classification tasks, especially medical imaging datasets',
      applicable_scenarios: ['Medical imaging classification', 'General image recognition', 'Transfer learning'],
      limitations: ['Higher memory footprint than plain ResNets', 'Training can be slower'],
      parameters: '~28M-200M',
      interpretability: 'Medium',
      resources: {
        docs: 'https://pytorch.org/vision/main/models/convnext.html',
        paper: 'https://arxiv.org/abs/2201.03545',
        code: 'https://github.com/facebookresearch/ConvNeXt'
      }
    },
    'resnet18': {
      architecture: 'ResNet-18',
      description: 'ResNet-18 is a convolutional neural network that is 18 layers deep. It introduces residual learning to ease the training of networks that are substantially deeper than those used previously.',
      complexity: 'Medium',
      use_case: 'Image classification, medical imaging, computer vision tasks',
      applicable_scenarios: ['General image classification', 'Resource-constrained environments', 'Baseline imaging model'],
      limitations: ['Lower accuracy compared to deeper ResNets (50, 101)', 'Limited capacity for very complex patterns'],
      parameters: '~11M',
      interpretability: 'Medium',
      resources: {
        docs: 'https://pytorch.org/vision/main/models/resnet.html',
        paper: 'https://arxiv.org/abs/1512.03385',
        code: 'https://github.com/pytorch/vision/blob/main/torchvision/models/resnet.py'
      }
    },
    'swintransformer': {
      architecture: 'Swin Transformer',
      description: 'Swin Transformer is a hierarchical Vision Transformer whose representation is computed with shifted windows. The shifted windowing scheme brings greater efficiency by limiting self-attention computation to non-overlapping local windows.',
      complexity: 'Medium',
      use_case: 'General purpose',
      applicable_scenarios: ['Image classification', 'Object detection', 'Semantic segmentation'],
      limitations: ['Complex architecture implementation', 'Sensitive to window size settings'],
      parameters: 'Unspecified',
      interpretability: 'Low',
      resources: {
        docs: 'https://pytorch.org/vision/main/models/swin_transformer.html',
        paper: 'https://arxiv.org/abs/2103.14030',
        code: 'https://github.com/microsoft/Swin-Transformer'
      }
    }
  };
  return imagingModels[modelId] || null;
};

  // Helper: Enrich model data
  const enrichModelWithMetadata = (backendModel, processingType) => {
    if (!backendModel || !backendModel.id) return backendModel;
    
    const modelId = backendModel.id;
    let metadata = {};

    if (processingType === 'segmentation') {
      const segMeta = getSegmentationModelMetadata(modelId);
      if (segMeta) {
        Object.assign(metadata, segMeta);
        metadata.name = segMeta.architecture; // Ensure name is set
      }
    } else if (processingType === 'tabular') {
      const tabMeta = getTabularModelMetadata(modelId);
      if (tabMeta) {
        Object.assign(metadata, tabMeta);
        metadata.name = tabMeta.architecture;
      }
    } else if (processingType === 'imaging') {
      const imgMeta = getImagingModelMetadata(modelId);
      if (imgMeta) {
        Object.assign(metadata, imgMeta);
        metadata.name = imgMeta.architecture;
      }
    } else {
      // Translation fallbacks for unknown types
      const safeTranslate = (key) => {
        const res = t(key);
        return res === key ? null : res;
      };
      
      let scenarios = safeTranslate(`models.${modelId}.applicableScenarios`, { returnObjects: true });
      if (!Array.isArray(scenarios)) scenarios = [];

      let limitations = safeTranslate(`models.${modelId}.limitations`, { returnObjects: true });
      if (!Array.isArray(limitations)) limitations = [];

      metadata = {
        name: backendModel.name || modelId,
        description: safeTranslate(`models.${modelId}.description`) || `Description for ${modelId}`,
        complexity: safeTranslate(`models.${modelId}.complexity`) || 'Medium',
        parameters: safeTranslate(`models.${modelId}.parameters`) || 'N/A',
        use_case: safeTranslate(`models.${modelId}.useCase`) || 'General purpose',
        applicable_scenarios: scenarios,
        limitations: limitations,
        interpretability: safeTranslate(`models.${modelId}.interpretability`) || 'Medium'
      };
    }

    return { ...backendModel, ...metadata, processingType };
  };

  // Prepare and filter models
  const processedModels = useMemo(() => {
    let allModels = [];
    const safeBackendModels = Array.isArray(models) ? models : [];
    const backendIds = safeBackendModels.map(m => m.id);

    // Iterate through defined types to build comprehensive list
    Object.entries(availableModels).forEach(([type, ids]) => {
      ids.forEach(id => {
        const backendModel = safeBackendModels.find(m => m.id === id) || { id, name: id };
        allModels.push(enrichModelWithMetadata(backendModel, type));
      });
    });

    // Filter
    let filtered = allModels;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(m => m.processingType === filterType);
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.name.toLowerCase().includes(lowerQuery) || 
        m.description?.toLowerCase().includes(lowerQuery) ||
        m.use_case?.toLowerCase().includes(lowerQuery)
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      const levels = { 'Low': 1, 'Medium': 2, 'High': 3 };

      switch (sortBy) {
        case 'name_asc': return a.name.localeCompare(b.name);
        case 'name_desc': return b.name.localeCompare(a.name);
        
        case 'complexity_asc':
          return (levels[a.complexity] || 2) - (levels[b.complexity] || 2);
        case 'complexity_desc':
          return (levels[b.complexity] || 2) - (levels[a.complexity] || 2);
          
        case 'type_asc': return a.processingType.localeCompare(b.processingType);
        case 'type_desc': return b.processingType.localeCompare(a.processingType);
        
        default: return 0;
      }
    });
  }, [models, availableModels, filterType, searchQuery, sortBy, t]);

  // Render a Model Card — compact, self-contained card for the responsive grid
  const ModelCard = ({ model }) => {
    const isExpanded = expandedModelId === model.id;
    const isCurrent = currentModel === model.id;

    // Visual Helper: Complexity Gauge (3 Bars) — bars + text label so color is not the only cue
    const ComplexityGauge = ({ status }) => {
       let level = 1;
       let color = 'var(--fx-success)';
       if (status === 'Medium') { level = 2; color = 'var(--fx-warning)'; }
       if (status === 'High')   { level = 3; color = 'var(--fx-danger)'; }
       const label = status || 'Low';

       return (
         <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
           <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 13 }} title={`Complexity: ${label}`} aria-label={`Complexity: ${label}`}>
             <Box sx={{ width: 4, height: '40%', bgcolor: level >= 1 ? color : 'var(--border-hairline)', borderRadius: '2px' }} />
             <Box sx={{ width: 4, height: '70%', bgcolor: level >= 2 ? color : 'var(--border-hairline)', borderRadius: '2px' }} />
             <Box sx={{ width: 4, height: '100%', bgcolor: level >= 3 ? color : 'var(--border-hairline)', borderRadius: '2px' }} />
           </Box>
           <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--fx-text-secondary)', lineHeight: 1 }}>
             {label}
           </Typography>
         </Box>
       );
    };

    // Type accent palette — category soft tints + mode-aware text tokens so the
    // icon tile / badge read correctly on light AND dark cards (1.1/1.2).
    const typeStyleByType = {
      tabular:      { bg: 'var(--fx-cat-indigo-soft)',  text: 'var(--fx-cat-indigo)',  border: 'var(--fx-cat-indigo-soft)',  rail: 'var(--fx-cat-indigo)',  label: 'Tabular',                  Icon: TabularIcon },
      imaging:      { bg: 'var(--fx-cat-emerald-soft)', text: 'var(--fx-cat-emerald)', border: 'var(--fx-cat-emerald-soft)', rail: 'var(--fx-cat-emerald)', label: 'Imaging · Classification', Icon: ImagingClassificationIcon },
      segmentation: { bg: 'var(--fx-cat-teal-soft)',    text: 'var(--fx-cat-teal)',    border: 'var(--fx-cat-teal-soft)',    rail: 'var(--fx-cat-teal)',    label: 'Imaging · Segmentation',  Icon: ImagingSegmentationIcon },
    };
    const ts = typeStyleByType[model.processingType] || { bg: 'var(--fx-info-soft)', text: 'var(--fx-info-text)', border: 'var(--fx-info-border)', rail: 'var(--fx-info-text)', label: model.processingType, Icon: AppsIcon };

    return (
      <Paper
        elevation={0}
        onClick={() => handleExpandClick(model.id)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          bgcolor: 'var(--fx-paper)',
          border: `1px solid ${isCurrent ? '#0087B4' : 'var(--fx-border)'}`,
          borderRadius: 2.5,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isExpanded ? 'var(--fx-shadow-lg)' : 'var(--fx-shadow-sm)',
          transition: 'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            borderColor: isCurrent ? '#0087B4' : 'var(--fx-border-strong)',
            boxShadow: 'var(--fx-shadow-lg)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* Header: icon tile + type chip */}
        <Box sx={{ px: 2, pt: 2, pb: 1.25, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box
            sx={{
              width: 40, height: 40,
              borderRadius: 1.5,
              bgcolor: ts.bg,
              color: ts.text,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': { fontSize: 20 },
            }}
          >
            <ts.Icon />
          </Box>
          <Chip
            label={ts.label}
            size="small"
            sx={{
              bgcolor: ts.bg,
              color: ts.text,
              fontWeight: 700,
              fontSize: '0.625rem',
              border: `1px solid ${ts.border}`,
              height: 20,
              maxWidth: '60%',
              '& .MuiChip-label': { px: 0.85, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        </Box>

        {/* Body: name + description */}
        <Box sx={{ px: 2, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--fx-text-strong)', lineHeight: 1.25, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>
              {model.name}
            </Typography>
            {isCurrent && (
              <Chip
                label="Selected"
                size="small"
                sx={{ height: 17, fontSize: '0.5625rem', bgcolor: 'rgba(0,135,180,0.10)', color: '#0087B4', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Box>
          <Typography
            sx={{
              color: 'var(--fx-text-secondary)',
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {model.description}
          </Typography>
        </Box>

        {/* Footer: stats + view affordance */}
        <Box sx={{ px: 2, py: 1.5, mt: 1.5, borderTop: '1px solid var(--fx-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Params */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MemoryIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
              <Typography sx={{ fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.6875rem' }}>
                {model.parameters}
              </Typography>
            </Box>
            {/* Complexity */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, borderLeft: '1px solid var(--fx-border)' }}>
              <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.6875rem', fontWeight: 500 }}>
                Complexity
              </Typography>
              <ComplexityGauge status={model.complexity} />
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, borderRadius: 1,
              color: isExpanded ? '#0087B4' : 'var(--fx-text-muted)',
              bgcolor: isExpanded ? 'rgba(0,135,180,0.10)' : 'transparent',
              transition: 'all 0.15s',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Paper>
    );
  };

  // ── Model Detail Drawer — opens on row click, slides in from the right ───
  // Does NOT affect list height (the main complaint with inline Collapse).
  const expandedModel = useMemo(
    () => processedModels.find((m) => m.id === expandedModelId) || null,
    [processedModels, expandedModelId]
  );

  const ModelDetailDrawer = () => {
    if (!expandedModel) return null;
    const model = expandedModel;
    const typeStyleByType = {
      tabular:      { bg: 'var(--fx-cat-indigo-soft)',  text: 'var(--fx-cat-indigo)',  border: 'var(--fx-cat-indigo-soft)',  label: 'Tabular',                  Icon: TabularIcon },
      imaging:      { bg: 'var(--fx-cat-emerald-soft)', text: 'var(--fx-cat-emerald)', border: 'var(--fx-cat-emerald-soft)', label: 'Imaging · Classification', Icon: ImagingClassificationIcon },
      segmentation: { bg: 'var(--fx-cat-teal-soft)',    text: 'var(--fx-cat-teal)',    border: 'var(--fx-cat-teal-soft)',    label: 'Imaging · Segmentation',  Icon: ImagingSegmentationIcon },
    };
    const ts = typeStyleByType[model.processingType] || { bg: 'var(--fx-info-soft)', text: 'var(--fx-info-text)', border: 'var(--fx-info-border)', label: model.processingType, Icon: AppsIcon };

    return (
      <Drawer
        anchor="right"
        open={!!expandedModelId}
        onClose={() => setExpandedModelId(null)}
        ModalProps={{
          keepMounted: false,
          slotProps: {
            backdrop: {
              sx: {
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(3px)',
              },
            },
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480, md: 560 },
            bgcolor: 'var(--surface-raised)',
            borderLeft: '1px solid var(--border-strong)',
          },
        }}
      >
        {/* Sticky header */}
        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'var(--surface-raised)', zIndex: 2, px: 2.5, py: 2, borderBottom: '1px solid var(--border-hairline)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: 1, bgcolor: ts.bg, color: ts.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  '& svg': { fontSize: 18 },
                }}
              >
                <ts.Icon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Learning model
                </Typography>
                <Typography sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                  {model.name}
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setExpandedModelId(null)} sx={{ color: 'var(--fx-text-secondary)' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              size="small"
              label={ts.label}
              sx={{ bgcolor: ts.bg, color: ts.text, border: `1px solid ${ts.border}`, fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
            />
            <Chip
              size="small"
              icon={<MemoryIcon sx={{ fontSize: '12px !important' }} />}
              label={model.parameters || 'Not set'}
              sx={{ bgcolor: 'var(--fx-subtle)', color: 'var(--fx-text-secondary)', border: '1px solid var(--fx-border)', fontWeight: 600, fontSize: '0.6875rem', height: 22, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', '& .MuiChip-icon': { color: 'var(--fx-text-secondary)' } }}
            />
            <Chip
              size="small"
              label={`Complexity · ${model.complexity || 'Not set'}`}
              sx={{ bgcolor: 'var(--fx-subtle)', color: 'var(--fx-text-secondary)', border: '1px solid var(--fx-border)', fontWeight: 600, fontSize: '0.6875rem', height: 22 }}
            />
            {currentModel === model.id && (
              <Chip
                size="small"
                label="Selected"
                sx={{ bgcolor: 'rgba(0,135,180,0.10)', color: '#0087B4', border: '1px solid rgba(0,135,180,0.25)', fontWeight: 700, fontSize: '0.6875rem', height: 22 }}
              />
            )}
          </Stack>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, bgcolor: 'var(--fx-subtle)' }}>
          <Stack spacing={1.5}>
            {/* Architecture & Logic */}
            <Box sx={{ bgcolor: 'var(--fx-paper)', border: '1px solid var(--fx-border)', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <MemoryIcon sx={{ fontSize: 14, color: 'var(--fx-text-secondary)' }} />
                <Typography sx={{ color: 'var(--fx-text-secondary)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.08em' }}>
                  Architecture & Logic
                </Typography>
              </Box>
              <Typography sx={{ color: 'var(--fx-text-secondary)', lineHeight: 1.65, fontSize: '0.8125rem' }}>
                {model.description}
              </Typography>
            </Box>

            {/* Ideal Use Cases */}
            <Box sx={{ bgcolor: 'var(--fx-paper)', border: '1px solid var(--fx-border)', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 14, color: 'var(--fx-text-secondary)' }} />
                <Typography sx={{ color: 'var(--fx-text-secondary)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.08em' }}>
                  Ideal Use Cases
                </Typography>
              </Box>
              <Typography sx={{ color: 'var(--fx-text-secondary)', lineHeight: 1.65, fontSize: '0.8125rem' }}>
                {model.use_case}
              </Typography>
            </Box>

            {/* Recommended scenarios. Semantic color lives only in the leading
                icon, the bullet markers, and the panel border (2.5); body text
                stays neutral so it reads calmly and works in light + dark. */}
            <Box sx={{ bgcolor: 'var(--fx-paper)', borderRadius: 2, border: '1px solid rgba(22, 163, 74, 0.30)', overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 0.9, bgcolor: 'rgba(22, 163, 74, 0.12)', display: 'flex', alignItems: 'center', gap: 0.75, borderBottom: '1px solid rgba(22, 163, 74, 0.30)' }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--fx-success)' }} />
                <Typography sx={{ color: 'var(--fx-success-text)', fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Recommended scenarios
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                {model.applicable_scenarios && model.applicable_scenarios.length > 0 ? (
                  <Stack spacing={0.75}>
                    {model.applicable_scenarios.map((scenario, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                        <Box sx={{ mt: '6px', width: 4, height: 4, borderRadius: '50%', bgcolor: 'var(--fx-success)', flexShrink: 0 }} />
                        <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.8125rem', lineHeight: 1.5 }}>{scenario}</Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.8125rem' }}>Not set</Typography>
                )}
              </Box>
            </Box>

            {/* Limitations. Same treatment as above with the danger hue. */}
            <Box sx={{ bgcolor: 'var(--fx-paper)', borderRadius: 2, border: '1px solid rgba(220, 38, 38, 0.30)', overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 0.9, bgcolor: 'rgba(220, 38, 38, 0.12)', display: 'flex', alignItems: 'center', gap: 0.75, borderBottom: '1px solid rgba(220, 38, 38, 0.30)' }}>
                <WarningIcon sx={{ fontSize: 14, color: 'var(--fx-danger)' }} />
                <Typography sx={{ color: 'var(--fx-danger-text)', fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Limitations
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                {model.limitations && model.limitations.length > 0 ? (
                  <Stack spacing={0.75}>
                    {model.limitations.map((limitation, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                        <Box sx={{ mt: '6px', width: 4, height: 4, borderRadius: '50%', bgcolor: 'var(--fx-danger)', flexShrink: 0 }} />
                        <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: '0.8125rem', lineHeight: 1.5 }}>{limitation}</Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.8125rem' }}>Not set</Typography>
                )}
              </Box>
            </Box>

            {/* Quick facts */}
            <Box sx={{ bgcolor: 'var(--fx-paper)', borderRadius: 2, border: '1px solid var(--fx-border)', p: 2 }}>
              <Typography sx={{ color: 'var(--fx-text-secondary)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6875rem', letterSpacing: '0.08em', mb: 1 }}>
                Key facts
              </Typography>
              {[
                ['Parameters', model.parameters || 'Not set', true],
                ['Complexity', model.complexity || 'Not set', false],
                ['Interpretability', model.interpretability || 'Not set', false],
              ].map(([k, v, mono], i, arr) => (
                <Stack
                  key={k}
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    py: 0.6,
                    borderBottom: i === arr.length - 1 ? 'none' : '1px dashed var(--fx-border)',
                  }}
                >
                  <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.75rem', fontWeight: 500 }}>{k}</Typography>
                  <Typography sx={{ fontFamily: mono ? 'var(--fx-font-mono)' : 'inherit', fontVariantNumeric: mono ? 'tabular-nums' : 'normal', color: 'var(--fx-text-strong)', fontSize: '0.8125rem', fontWeight: 600 }}>
                    {v}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Box>

        {/* Sticky footer — resources */}
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--fx-border)', bgcolor: 'var(--fx-paper)', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { href: model.resources?.docs,  Icon: DescriptionOutlinedIcon, label: 'Documentation' },
            { href: model.resources?.paper, Icon: MenuBookIcon,            label: 'Paper' },
            { href: model.resources?.code,  Icon: CodeIcon,                label: 'Source code' },
          ].map(({ href, Icon, label }) => (
            <Box
              key={label}
              component={href ? 'a' : 'span'}
              href={href || undefined}
              target={href ? '_blank' : undefined}
              rel={href ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                px: 1.25, py: 0.6, borderRadius: 1,
                border: '1px solid var(--fx-border)', bgcolor: 'var(--fx-paper)',
                color: href ? 'var(--fx-text-primary)' : 'var(--fx-text-muted)',
                cursor: href ? 'pointer' : 'default',
                textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600,
                pointerEvents: href ? 'auto' : 'none',
                transition: 'all 0.15s ease',
                '&:hover': href ? { bgcolor: 'rgba(27,54,93,0.05)', borderColor: '#0087B4', color: '#0087B4' } : {},
              }}
            >
              <Icon sx={{ fontSize: 14 }} />
              {label}
              {href && <OpenInNewIcon sx={{ fontSize: 11, opacity: 0.7 }} />}
            </Box>
          ))}
        </Box>
      </Drawer>
    );
  };

  // Calculate counts for badges
  const modelCounts = useMemo(() => {
     return {
        all: Object.values(availableModels).flat().length,
        tabular: availableModels.tabular.length,
        imaging: availableModels.imaging.length,
        segmentation: availableModels.segmentation.length
     };
  }, [availableModels]);

  // Filter chip labels match the task taxonomy used on /datasets and /configs.
  // Internal `value` strings stay as the canonical pipeline keys.
  const filterChips = [
     { label: 'All Models', value: 'all', count: modelCounts.all, icon: <AppsIcon fontSize="small" /> },
     { label: 'Tabular', value: 'tabular', count: modelCounts.tabular, icon: <TabularIcon /> },
     { label: 'Imaging · Classification', value: 'imaging', count: modelCounts.imaging, icon: <ImagingClassificationIcon /> },
     { label: 'Imaging · Segmentation', value: 'segmentation', count: modelCounts.segmentation, icon: <ImagingSegmentationIcon /> },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: 'auto' }}>
      {/* Page Header — title only; controls live in the unified toolbar below */}
      <HeaderWithControls
        icon={<PsychologyAltOutlinedIcon />}
        title={t('models.title')}
        subtitle="Discover and manage available models for your federated learning tasks."
      />

      {/* Content Area */}
      {loading ? (
        <LoadingState label="Loading models…" minHeight={220} />
      ) : (
        <Box>
          {/* Unified one-line toolbar: filters + count · search + sort */}
          <PageToolbar
            tabs={filterChips}
            filterValue={filterType}
            onFilterChange={setFilterType}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchPlaceholder="Search models..."
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOptions={[
              <MenuItem key="name_asc" value="name_asc">Name (A-Z)</MenuItem>,
              <MenuItem key="name_desc" value="name_desc">Name (Z-A)</MenuItem>,
              <Divider key="div1" />,
              <MenuItem key="complexity_asc" value="complexity_asc">Complexity (Low-High)</MenuItem>,
              <MenuItem key="complexity_desc" value="complexity_desc">Complexity (High-Low)</MenuItem>,
              <Divider key="div2" />,
              <MenuItem key="type_asc" value="type_asc">Type (A-Z)</MenuItem>,
              <MenuItem key="type_desc" value="type_desc">Type (Z-A)</MenuItem>
            ]}
            countText={`${processedModels.length} ${processedModels.length === 1 ? 'model' : 'models'}`}
          />

          {processedModels.length > 0 ? (
            <Grid container spacing={2}>
              {processedModels.map(model => (
                <Grid item xs={12} sm={6} lg={4} key={model.id} sx={{ display: 'flex' }}>
                  <ModelCard model={model} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper variant="outlined" sx={{ bgcolor: 'var(--fx-paper)', borderStyle: 'dashed', borderColor: 'var(--fx-border-strong)' }}>
              <EmptyState
                icon={<ModelTrainingIcon sx={{ fontSize: 22 }} />}
                title="No models found"
                description="Try clearing your filters or search criteria."
                minHeight={140}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Right-side detail drawer — does NOT affect list height */}
      <ModelDetailDrawer />
    </Box>
  );
};

export default Models;